import { roundMoney } from "./pricing";
import { isChargeable } from "./groupPlates";
import type {
  Invoice,
  InvoiceItem,
  SettlementMethod,
} from "@/lib/api/types";

/**
 * The client's mirror of the server's refund arithmetic (R3, R4).
 *
 * The authority is
 * `apps/backend/src/modules/restaurant/policies/refund-allocation.policy.ts`
 * and nothing here re-decides anything: the server allocates every plate's
 * share, writes it onto the ledger row, and the till never sends an amount at
 * all (§8.2 — `refundInvoice`'s payload has no amount field, on purpose, which
 * is what makes R2 enforceable rather than merely stated).
 *
 * This module exists for the one reason `pricing.ts` exists: **the screen has
 * to say a figure the server has not been asked for yet.** A cashier ticking
 * three plates on the refund sheet must read what the customer will get back —
 * and what of it is net, service charge and VAT — BEFORE the command goes out,
 * not off a toast afterwards. That is trap 1: the composition rule has to be on
 * screen, in numbers, not in a comment.
 *
 * Because it quotes money that will be paid out, it is a CHARACTER-FOR-CHARACTER
 * mirror of the policy — the same two passes, the same cap, the same reverse
 * remainder walk, the same `roundMoney` (never a second rounding helper, which
 * is exactly how two screens come to disagree by a piastre).
 *
 * ONE deliberate divergence, and it is in the failure mode, never the
 * arithmetic: the server THROWS on its two preconditions, because reaching them
 * there is a programmer error on a money command. Here they return `null`. A
 * till mid-service must not white-screen because a bill's plates fail to sum to
 * its stored subtotal; the sheet degrades to "the till cannot state the split,
 * the server will" and the refund still goes out — the server is the authority
 * either way.
 */

/** The stored invoice figures the allocation is a function of. */
export type RefundAllocationInvoice = {
  /** `B` — the chargeable-plate subtotal, which the plates themselves sum to. */
  subtotal: number;
  serviceChargeTotal: number;
  vatTotal: number;
  /** The invoice's OWN snapshot (D33), never the live setting. */
  pricesIncludeTax: boolean;
  /**
   * D146 — the food after the bill's discount at the bill, in `subtotal`'s
   * basis, when it carries one: the plates are pro-rated onto it first (the
   * server's one rule). Absent on every bill without a discount.
   */
  discountedSubtotal?: number;
};

/** One chargeable plate. `isChargeable` is the caller's filter, not this one's. */
export type RefundAllocatableItem = {
  id: string;
  lineTotal: number;
};

/** A plate's share, stated POSITIVE. The ledger row negates it. */
export type RefundShare = {
  itemId: string;
  /** What the customer gets back for this plate. */
  amount: number;
  netAmount: number;
  serviceChargeAmount: number;
  vatAmount: number;
};

/**
 * Every chargeable plate's share of the bill, keyed by item id — or `null` when
 * the input cannot be honestly answered.
 *
 * MUST be given the WHOLE chargeable set of the invoice, never just the plates
 * being refunded now (the policy's property 4: both passes run over the whole
 * set and read nothing about the subset, which is what makes three partial
 * refunds over three weeks still sum to exactly the bill's stored figures).
 * The caller picks the requested ids out of the returned map.
 */
export function allocateInvoiceRefundShares(
  invoice: RefundAllocationInvoice,
  chargeableItems: readonly RefundAllocatableItem[],
): Map<string, RefundShare> | null {
  // 1. `B <= 0` — a divide by zero. The server refuses `total <= 0` before it
  //    ever allocates, so this is the fully-comped bill and every other shape
  //    with nothing to give back.
  const basis = invoice.subtotal;
  if (!(basis > 0)) return null;

  // 2. `Σ L_k ≠ B` — the plates handed in are not the whole chargeable set.
  //    Summed with the SAME progressive rounding pass 1 uses, so what is
  //    checked is literally `P_n = B`.
  let itemsTotal = 0;
  for (const plate of chargeableItems) {
    itemsTotal = roundMoney(itemsTotal + plate.lineTotal);
  }
  if (itemsTotal !== basis) return null;

  // The order is OURS, not the caller's: the whole telescoping argument rests
  // on the order being identical on every call, three weeks apart.
  const sorted = [...chargeableItems].sort((a, b) =>
    a.id < b.id ? -1 : a.id > b.id ? 1 : 0,
  );

  // D146 — pro-rate onto the discounted base, cumulatively, exactly as the
  // policy does: `L'_k = round(B'·P_k/B) − round(B'·P_{k−1}/B)`.
  const discounted = invoice.discountedSubtotal;
  let basisAfter = basis;
  let plates: RefundAllocatableItem[] = sorted;
  if (discounted !== undefined && discounted !== basis) {
    if (!(discounted > 0) || discounted > basis) return null;
    let rawPrefix = 0;
    let cumulative = 0;
    plates = sorted.map((plate) => {
      rawPrefix = roundMoney(rawPrefix + plate.lineTotal);
      const next = roundMoney((discounted * rawPrefix) / basis);
      const lineTotal = roundMoney(next - cumulative);
      cumulative = next;
      return { id: plate.id, lineTotal };
    });
    basisAfter = discounted;
  }

  const inclusive = invoice.pricesIncludeTax;

  // ---- Pass 1: the capped cumulative rule --------------------------------
  const rows: Array<{
    itemId: string;
    amount: number;
    net: number;
    service: number;
    vat: number;
  }> = [];

  let prefix = 0;
  let cumService = 0;
  let cumVat = 0;

  for (const plate of plates) {
    prefix = roundMoney(prefix + plate.lineTotal);

    // `+∞` in exclusive mode, where `net_k = L_k ≥ 0` identically, so both
    // passes are provable no-ops and the uncapped rule is reproduced exactly.
    const cap = inclusive ? plate.lineTotal : Number.POSITIVE_INFINITY;

    const rawService = roundMoney(
      (invoice.serviceChargeTotal * prefix) / basisAfter,
    );
    const cappedService = Math.min(rawService, cumService + cap);
    // Rounded because a difference of two 2dp floats is not itself 2dp.
    const service = roundMoney(cappedService - cumService);
    cumService = roundMoney(cumService + service);

    const rawVat = roundMoney((invoice.vatTotal * prefix) / basisAfter);
    const cappedVat = Math.min(rawVat, cumVat + (cap - service));
    const vat = roundMoney(cappedVat - cumVat);
    cumVat = roundMoney(cumVat + vat);

    const amount = inclusive
      ? plate.lineTotal
      : roundMoney(plate.lineTotal + service + vat);
    const net = roundMoney(amount - service - vat);

    rows.push({ itemId: plate.id, amount, net, service, vat });
  }

  // ---- Pass 2: place what the cap held back ------------------------------
  // Reverse, so the piastre lands on the LATEST plate with net to give.
  let outstandingService = roundMoney(invoice.serviceChargeTotal - cumService);
  let outstandingVat = roundMoney(invoice.vatTotal - cumVat);

  for (let k = rows.length - 1; k >= 0; k -= 1) {
    const row = rows[k];

    if (outstandingService > 0 && row.net > 0) {
      const delta = roundMoney(Math.min(outstandingService, row.net));
      row.service = roundMoney(row.service + delta);
      row.net = roundMoney(row.net - delta);
      outstandingService = roundMoney(outstandingService - delta);
    }

    if (outstandingVat > 0 && row.net > 0) {
      const delta = roundMoney(Math.min(outstandingVat, row.net));
      row.vat = roundMoney(row.vat + delta);
      row.net = roundMoney(row.net - delta);
      outstandingVat = roundMoney(outstandingVat - delta);
    }

    if (outstandingService <= 0 && outstandingVat <= 0) break;
  }

  const shares = new Map<string, RefundShare>();
  for (const row of rows) {
    shares.set(row.itemId, {
      itemId: row.itemId,
      amount: row.amount,
      netAmount: row.net,
      serviceChargeAmount: row.service,
      vatAmount: row.vat,
    });
  }
  return shares;
}

/**
 * The whole chargeable set of a bill, in the shape the allocation takes.
 *
 * The SAME `isChargeable` the till's own totals read (D16, both axes) — a
 * comped plate was never charged for and a voided one is off the bill, so
 * neither has a share to give back and neither is in the basis.
 */
export function refundableItems(invoice: Invoice): InvoiceItem[] {
  return invoice.items.filter((item) => isChargeable(item));
}

/**
 * The plates the cashier may actually TICK — the chargeable set minus the ones
 * the ledger says already came back (R12/§10, `InvoiceItem.refunded`).
 *
 * A SECOND function rather than a narrower `refundableItems`, and that is the
 * whole point: the allocation basis must stay the WHOLE chargeable set (§6.1,
 * property 4). Refunding one plate of three does not make the other two worth
 * more, so an already-refunded plate is still part of what the shares are
 * computed over — narrowing the basis would inflate every remaining plate's
 * share and quote a refund larger than the bill.
 *
 * So: `refundableItems` feeds the ALLOCATION, this feeds the PICKER. The sheet
 * still lists the refunded plate, greyed and labelled, because a bill that
 * silently grew shorter between two openings of the sheet is how a cashier
 * loses track of what was given back.
 *
 * This does NOT replace the server's typed `RESTAURANT_ITEM_ALREADY_REFUNDED`
 * refusal, and must not be read as one: two tills can open this sheet on the
 * same bill at the same moment, and only the partial unique index on the ledger
 * arbitrates that. This is what stops the till offering the money a second time
 * and then apologising.
 */
export function pickableRefundItems(invoice: Invoice): InvoiceItem[] {
  return refundableItems(invoice).filter((item) => !item.refunded);
}

/**
 * THE METHODS A REFUND MAY GO BACK BY (R6) — the till's mirror of the server's
 * `REFUNDABLE_SETTLEMENT_METHODS`, and an ALLOWLIST for the same reason.
 *
 * `CASH` comes out of this drawer, `CARD` back to the card, `INSTAPAY` (D44)
 * back by transfer out of the bank — and each of the three has a sentence on
 * the refund sheet saying which, because the drawer consequence differs and
 * trap 1 says the rule lives on screen.
 *
 * IT MUST NOT BECOME `!== "WRITE_OFF"`. That is what it used to be, and the
 * cost was exact: the day master added `INSTAPAY` the till began OFFERING a
 * refund the API refused with `RESTAURANT_REFUND_METHOD_NOT_REFUNDABLE`, whose
 * copy was written for a walk-out — so a cashier who had already picked plates
 * and confirmed was told "this bill was closed unpaid, so there is no money to
 * give back" about a bill paid in full into the bank, and the floor fell back to
 * the untracked drawer payout this whole feature exists to end. A denylist
 * offers every future settlement method the moment someone adds it; this list
 * offers only what the server has agreed to pay out.
 */
export const REFUNDABLE_SETTLEMENT_METHODS = [
  "CASH",
  "CARD",
  "INSTAPAY",
] as const satisfies readonly SettlementMethod[];

/** The methods above, as a type — what `RefundSheet` and the slip may name. */
export type RefundableSettlementMethod =
  (typeof REFUNDABLE_SETTLEMENT_METHODS)[number];

export function isRefundableSettlementMethod(
  method: string | null,
): method is RefundableSettlementMethod {
  return (
    method !== null &&
    (REFUNDABLE_SETTLEMENT_METHODS as readonly string[]).includes(method)
  );
}

/**
 * Is there a refund to OFFER on this bill at all — the till's button, the back
 * office's route into the till, and the sentence that replaces both when the
 * rung is missing (business GAP-1).
 *
 * The condition is the real one and not a proxy for it: money can only go back
 * off a SETTLED bill (R1), by a method money actually came in by (R6 — a
 * WRITE_OFF took none, so there is none to return; an unrecognised method has
 * not been decided about), when the bill is worth something (a fully comped
 * bill is refused as `NOTHING_TO_REFUND`), and when at least one plate has NOT
 * already come back — which the ledger now answers per plate through
 * `InvoiceItem.refunded`, so this no longer has to be approximated by
 * `refundTotal < total`.
 *
 * The method arm is the ALLOWLIST above, matching the server's, so the till
 * never invites an action the API will reject. That correspondence is the whole
 * point: any refusal this predicate cannot predict reaches the cashier AFTER
 * they have picked the plates and pressed confirm.
 *
 * The permission is the CALLER's to check: this answers "is there anything to
 * refund", never "may this person refund it".
 */
export function isRefundOfferable(invoice: Invoice): boolean {
  return (
    invoice.status === "SETTLED" &&
    isRefundableSettlementMethod(invoice.settlementMethod) &&
    invoice.total > 0 &&
    pickableRefundItems(invoice).length > 0
  );
}

/** A selection's total, summed component by component. */
export function sumRefundShares(
  shares: Map<string, RefundShare>,
  itemIds: readonly string[],
): RefundShare {
  let amount = 0;
  let netAmount = 0;
  let serviceChargeAmount = 0;
  let vatAmount = 0;
  for (const itemId of itemIds) {
    const share = shares.get(itemId);
    if (!share) continue;
    amount = roundMoney(amount + share.amount);
    netAmount = roundMoney(netAmount + share.netAmount);
    serviceChargeAmount = roundMoney(
      serviceChargeAmount + share.serviceChargeAmount,
    );
    vatAmount = roundMoney(vatAmount + share.vatAmount);
  }
  return { itemId: "", amount, netAmount, serviceChargeAmount, vatAmount };
}

/**
 * R8's threshold, mirrored from
 * `apps/backend/src/modules/restaurant/policies/refund-late.policy.ts`.
 *
 * The FALLBACK, not the rule. The per-store
 * `restaurant_settings.refund_late_after_minutes` is on `RestaurantSettingsView`
 * and on `UpdateSettingsDto`, the general-settings screen edits it, and the
 * sheet prefers the store's value (`InvoicePage` → `RefundSheet`'s
 * `lateAfterMinutes`). This constant is what the sheet warns against only while
 * that read is in flight or has failed — and because it IS the server's own
 * default, a sheet that falls back still agrees with the flag the ledger will
 * store.
 */
export const DEFAULT_REFUND_LATE_AFTER_MINUTES = 240;

/**
 * Is this refund late (R8)?
 *
 * `>=` rather than `>`, the server's comparison exactly: "four hours or more"
 * is a boundary a manager can say out loud. It FLAGS and never refuses — the
 * sheet shows the warning and keeps Confirm enabled, because a refund the
 * product refuses is a refund that happens anyway as an untracked cash payout.
 */
export function isRefundLate(
  settledAt: Date,
  occurredAt: Date,
  thresholdMinutes: number,
): boolean {
  const elapsedMinutes =
    (occurredAt.getTime() - settledAt.getTime()) / (60 * 1000);
  return elapsedMinutes >= thresholdMinutes;
}

/** The threshold in words, for the sheet's warning: hours when it divides. */
export function thresholdInHours(thresholdMinutes: number): number | null {
  return thresholdMinutes % 60 === 0 ? thresholdMinutes / 60 : null;
}
